/**
 * Assessment-derived classification tags for client roster display and filtering.
 * Tags are read-only — sourced from client.classification and/or latest posture analysis.
 */

import type { Client, PostureAnalysis } from '@/lib/types';
import { safeToDate } from '@/lib/utils';
import type { CachedPostureAnalysis, ClientClassificationCacheSlice } from './types';

export type { ClientClassificationCacheSlice } from './types';

export const CLASSIFICATION_ORIENTATION_TAGS = ['APT', 'SB'] as const;
export const CLASSIFICATION_LATERALIZATION_TAGS = ['R Lat', 'L Lat'] as const;
export const CLASSIFICATION_GROUNDING_TAGS = ['R Grd', 'L Grd'] as const;
export const CLASSIFICATION_PHASE_TAGS = ['P1', 'P2', 'P3'] as const;

export const CLASSIFICATION_TAG_IDS = [
  ...CLASSIFICATION_ORIENTATION_TAGS,
  ...CLASSIFICATION_LATERALIZATION_TAGS,
  ...CLASSIFICATION_GROUNDING_TAGS,
  ...CLASSIFICATION_PHASE_TAGS,
] as const;

export type ClassificationTagId = (typeof CLASSIFICATION_TAG_IDS)[number];

export type ClassificationTagDimension =
  | 'orientation'
  | 'lateralization'
  | 'grounding'
  | 'phase';

export interface ClassificationTag {
  id: ClassificationTagId;
  label: ClassificationTagId;
  dimension: ClassificationTagDimension;
}

const TAG_DIMENSION: Record<ClassificationTagId, ClassificationTagDimension> = {
  APT: 'orientation',
  SB: 'orientation',
  'R Lat': 'lateralization',
  'L Lat': 'lateralization',
  'R Grd': 'grounding',
  'L Grd': 'grounding',
  P1: 'phase',
  P2: 'phase',
  P3: 'phase',
};

/**
 * Solid tag colors aligned with the drill-library taxonomy palette
 * (`src/config/RuleDoc-Drill-Taxonomy.json` → colors). Phase codes use the exact
 * taxonomy option colors; orientation/lateralization/grounding reuse taxonomy
 * category hues so classification tags read like drill-library tags.
 */
export const CLASSIFICATION_TAG_COLORS: Record<ClassificationTagId, string> = {
  // Orientation → "Posture" category (#B91C1C)
  APT: '#B91C1C',
  SB: '#9F1239',
  // Lateralization → "Planes & Turn Bias" hue (#9333EA)
  'R Lat': '#9333EA',
  'L Lat': '#7E22CE',
  // Grounding → "Foot / Lower-Limb Bias" hue (#0F766E)
  'R Grd': '#0F766E',
  'L Grd': '#115E59',
  // Phase → exact taxonomy option colors
  P1: '#1D4ED8',
  P2: '#0E7490',
  P3: '#6D28D9',
};

export function getClassificationTagDimension(id: ClassificationTagId): ClassificationTagDimension {
  return TAG_DIMENSION[id];
}

export function getClassificationTagColor(id: ClassificationTagId): string {
  return CLASSIFICATION_TAG_COLORS[id];
}

/**
 * Full human-readable expansion of each classification abbreviation. Used for the
 * badge `title`/`aria-label` so non-expert and non-native-English staff aren't left
 * guessing what "APT" / "R Grd" / "P2" mean (clinical data-integrity + a11y).
 */
export const CLASSIFICATION_TAG_TITLES: Record<ClassificationTagId, string> = {
  APT: 'Anterior Pelvic Tilt',
  SB: 'Swayback',
  'R Lat': 'Right Lateralization',
  'L Lat': 'Left Lateralization',
  'R Grd': 'Right Grounding',
  'L Grd': 'Left Grounding',
  P1: 'Phase 1',
  P2: 'Phase 2',
  P3: 'Phase 3',
};

export function getClassificationTagTitle(id: ClassificationTagId): string {
  return CLASSIFICATION_TAG_TITLES[id];
}


function makeTag(id: ClassificationTagId): ClassificationTag {
  return { id, label: id, dimension: TAG_DIMENSION[id] };
}

function normalizeToken(raw: string): ClassificationTagId | null {
  const token = raw.trim();
  if (!token) return null;

  const upper = token.toUpperCase();
  if (upper === 'APT' || upper.includes('ANTERIOR')) return 'APT';
  if (upper === 'SB' || upper.includes('SWAYBACK')) return 'SB';

  if (/^R\s*LAT$/i.test(token) || token === 'R Lat') return 'R Lat';
  if (/^L\s*LAT$/i.test(token) || token === 'L Lat') return 'L Lat';
  if (/^R\s*GRD$/i.test(token) || token === 'R Grd') return 'R Grd';
  if (/^L\s*GRD$/i.test(token) || token === 'L Grd') return 'L Grd';

  if (upper.startsWith('P1') || upper === 'P1-APT' || upper === 'P1-SB') return 'P1';
  if (upper.startsWith('P2')) return 'P2';
  if (upper.startsWith('P3')) return 'P3';

  if (CLASSIFICATION_TAG_IDS.includes(token as ClassificationTagId)) {
    return token as ClassificationTagId;
  }

  return null;
}

/** Parse a stored classification string (comma-separated) into canonical badge tags. */
export function parseClassificationString(
  classification?: string | null
): ClassificationTag[] {
  if (!classification?.trim()) return [];

  const seen = new Set<ClassificationTagId>();
  const tags: ClassificationTag[] = [];

  for (const part of classification.split(',')) {
    const id = normalizeToken(part);
    if (id && !seen.has(id)) {
      seen.add(id);
      tags.push(makeTag(id));
    }
  }

  return tags;
}

function mapPatternToOrientation(pattern: string | null | undefined): ClassificationTagId | null {
  if (!pattern) return null;
  const p = pattern.toLowerCase();
  if (p.includes('swayback') || p === 'sb') return 'SB';
  if (p.includes('anterior') || p === 'apt') return 'APT';
  if (p === 'n' || p.includes('neutral')) return null;
  const fromToken = normalizeToken(pattern);
  if (fromToken === 'APT' || fromToken === 'SB') return fromToken;
  return null;
}

function mapLateralization(value: string | null | undefined): ClassificationTagId | null {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v.includes('right') || v === 'r lat') return 'R Lat';
  if (v.includes('left') || v === 'l lat') return 'L Lat';
  return normalizeToken(value);
}

function mapGrounding(side: 'Left' | 'Right' | undefined): ClassificationTagId | null {
  if (side === 'Left') return 'L Grd';
  if (side === 'Right') return 'R Grd';
  return null;
}

/** Derive tags from a posture analysis classification object. */
export function tagsFromPostureClassification(
  classification: NonNullable<PostureAnalysis['classification']>
): ClassificationTag[] {
  const seen = new Set<ClassificationTagId>();
  const tags: ClassificationTag[] = [];

  const add = (id: ClassificationTagId | null) => {
    if (id && !seen.has(id)) {
      seen.add(id);
      tags.push(makeTag(id));
    }
  };

  add(mapPatternToOrientation(classification.pattern));
  add(mapLateralization(classification.lateralized));
  add(mapGrounding(classification.groundingSide));

  return tags;
}

function analysisTimestamp(analysis: CachedPostureAnalysis): number {
  return safeToDate(analysis.createdAt)?.getTime() ?? 0;
}

function getLatestPostureAnalysisForClient(
  clientId: string,
  analyses?: CachedPostureAnalysis[]
): CachedPostureAnalysis | null {
  if (!analyses?.length) return null;

  const clientAnalyses = analyses
    .filter((a) => a.clientId === clientId)
    .sort((a, b) => analysisTimestamp(b) - analysisTimestamp(a));

  return clientAnalyses[0] ?? null;
}

const DIMENSION_ORDER: ClassificationTagDimension[] = [
  'orientation',
  'lateralization',
  'grounding',
  'phase',
];

/**
 * Collapse tags to at most one per dimension (orientation, lateralization,
 * grounding, phase are each mutually exclusive). Earlier sources win, so callers
 * should pass the authoritative source first. Output is sorted by dimension for
 * stable badge order. Prevents contradictions like both `L Lat` and `R Lat`.
 */
function collapseByDimension(tags: ClassificationTag[]): ClassificationTag[] {
  const byDimension = new Map<ClassificationTagDimension, ClassificationTag>();
  for (const tag of tags) {
    if (!byDimension.has(tag.dimension)) {
      byDimension.set(tag.dimension, tag);
    }
  }
  return DIMENSION_ORDER.map((dimension) => byDimension.get(dimension)).filter(
    (tag): tag is ClassificationTag => Boolean(tag)
  );
}

/**
 * Resolve read-only classification badges for a client.
 * Prefers latest cached posture analysis classification when present, then fills
 * remaining dimensions (e.g. phase) from the stored classification string. Each
 * dimension yields at most one badge, so a client can never appear as both
 * left- and right-lateralized.
 */
export function getClientClassificationTags(
  client: Pick<Client, 'id' | 'classification'>,
  cache?: ClientClassificationCacheSlice
): ClassificationTag[] {
  const latest = getLatestPostureAnalysisForClient(client.id, cache?.allPostureAnalyses);
  const fromString = parseClassificationString(client.classification);

  if (latest?.classification) {
    const fromPosture = tagsFromPostureClassification(latest.classification);
    // Posture analysis is authoritative; the string only fills dimensions it omits.
    return collapseByDimension([...fromPosture, ...fromString]);
  }

  return collapseByDimension(fromString);
}

/** True when client tags include every selected filter tag (AND semantics). */
export function clientMatchesClassificationFilters(
  tags: ClassificationTag[],
  selectedFilters: ClassificationTagId[]
): boolean {
  return clientMatchesClassificationFilterIds(
    tags.map((t) => t.id),
    selectedFilters
  );
}

export function clientMatchesClassificationFilterIds(
  tagIds: ClassificationTagId[],
  selectedFilters: ClassificationTagId[]
): boolean {
  if (selectedFilters.length === 0) return true;
  const set = new Set(tagIds);

  // Faceted semantics: OR within a dimension, AND across dimensions. Each
  // dimension yields at most one tag per client (see collapseByDimension), so a
  // pure AND would make selecting two tags of the same dimension (e.g. APT + SB,
  // or R Lat + L Lat) match nothing and silently empty the roster. Instead, a
  // client matches when, for every dimension the user filtered on, it carries at
  // least one of the selected tags in that dimension.
  const byDimension = new Map<ClassificationTagDimension, ClassificationTagId[]>();
  for (const filter of selectedFilters) {
    const dimension = TAG_DIMENSION[filter];
    const list = byDimension.get(dimension);
    if (list) list.push(filter);
    else byDimension.set(dimension, [filter]);
  }

  for (const filters of byDimension.values()) {
    if (!filters.some((filter) => set.has(filter))) return false;
  }
  return true;
}

export function countActiveClassificationFilters(
  selectedFilters: ClassificationTagId[]
): number {
  return selectedFilters.length;
}
